import { useId, useState } from 'react'
import { krakenAPI, krakenError } from '../../services/krakenApi'
import type { LiveTrade } from '../../types/kraken'
import useEscapeKey from '../../hooks/useEscapeKey'
import { formatPrice } from '../../utils/marketPrice'
import Spinner from '../Spinner'
import { LiveTradeStatusBadge } from './tradeFormat'

type Action = 'stop' | 'target' | 'cancel' | 'close'

interface ManageExitModalProps {
  trade: LiveTrade
  /** Open straight on one action (the card's "Close position" button). */
  initialAction?: Action
  onClose: () => void
  onChanged: (trade: LiveTrade) => void
}

const CONFIRM_COPY: Record<Action, (t: LiveTrade, price: number) => string> = {
  stop: (t, p) => `Move the stop on Kraken from $${formatPrice(t.stop_price)} to $${formatPrice(p)}?`,
  target: (t, p) =>
    `Change the target from $${formatPrice(t.target_price)} to $${formatPrice(p)}? FinCreator sells when Kraken's price reaches it.`,
  cancel: (t) =>
    `Cancel the pending entry on Kraken? ${t.filled_qty > 0 ? `The ${t.filled_qty} already filled stays open.` : 'No position has been opened.'}`,
  close: (t) =>
    `Sell ${t.held_qty} ${t.symbol.split('/')[0]} at market on Kraken now? This is a real order and closes the position at whatever price Kraken fills.`,
}

/**
 * Move stop / change target / cancel pending entry / close now.
 *
 * Each action is confirmed first, and the card only changes once the server
 * returns Kraken's verified state — an amend Kraken refused leaves the old
 * stop on screen, because it is still the stop on Kraken.
 */
const ManageExitModal = ({ trade, initialAction, onClose, onChanged }: ManageExitModalProps) => {
  const titleId = useId()
  const [current, setCurrent] = useState(trade)
  const [stop, setStop] = useState(String(trade.stop_price))
  const [target, setTarget] = useState(String(trade.target_price))
  const [pending, setPending] = useState<Action | null>(initialAction ?? null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)

  useEscapeKey(onClose, !busy)

  const entryPending = current.status === 'pending_fill' || current.status === 'partially_filled'
  const holding = current.held_qty > 0 && (current.status === 'open' || current.status === 'partially_filled')
  const priceFor = (a: Action) => Number(a === 'stop' ? stop : target)

  const run = async (action: Action) => {
    setBusy(true)
    setError(null)
    setDone(null)
    try {
      const next =
        action === 'stop'
          ? await krakenAPI.moveStop(current.id, priceFor('stop'))
          : action === 'target'
            ? await krakenAPI.changeTarget(current.id, priceFor('target'))
            : action === 'cancel'
              ? await krakenAPI.cancelEntry(current.id)
              : await krakenAPI.close(current.id)
      setCurrent(next)
      onChanged(next)
      setDone(
        action === 'stop'
          ? `Kraken confirmed the stop at $${formatPrice(next.stop_price)}.`
          : action === 'target'
            ? `Target set to $${formatPrice(next.target_price)}.`
            : `Kraken reports: ${next.status_label}.`
      )
      setPending(null)
    } catch (e) {
      setError(krakenError(e, 'Kraken did not accept the change. Nothing was changed.'))
      setPending(null)
    } finally {
      setBusy(false)
    }
  }

  const input =
    'w-full rounded-md border border-terminal-border bg-terminal-bg px-3 py-2 font-mono text-gray-50 focus:border-primary-500 focus:outline-none'
  const button =
    'rounded-md px-3 py-2 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-40 whitespace-nowrap'

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 sm:items-center sm:p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="max-h-[95vh] w-full max-w-md overflow-y-auto rounded-t-xl border border-terminal-border bg-terminal-surface p-4 text-gray-100 sm:rounded-xl sm:p-6"
      >
        <div className="mb-3 flex items-start justify-between gap-3">
          <div>
            <h2 id={titleId} className="text-lg font-bold text-gray-50">
              Manage exit — {current.symbol}
            </h2>
            <div className="mt-1">
              <LiveTradeStatusBadge trade={current} />
            </div>
          </div>
          {!busy && (
            <button type="button" onClick={onClose} aria-label="Close" className="rounded px-2 py-1 text-gray-400 hover:bg-white/5">
              ✕
            </button>
          )}
        </div>

        <p className="mb-4 text-xs text-gray-400">
          Stop {current.stop_on_kraken ? 'resting on Kraken' : 'not on Kraken yet'} · target watched by FinCreator.
        </p>

        {pending ? (
          <div className="space-y-3 rounded-md border border-amber-500/50 bg-amber-500/10 p-3">
            <p className="text-sm text-amber-100">{CONFIRM_COPY[pending](current, priceFor(pending))}</p>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={busy}
                onClick={() => setPending(null)}
                className={`${button} flex-1 border border-terminal-border text-gray-300 hover:bg-white/5`}
              >
                Back
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => run(pending)}
                className={`${button} flex flex-1 items-center justify-center gap-2 ${
                  pending === 'close' ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-primary-600 text-white hover:bg-primary-700'
                }`}
              >
                {busy && <Spinner size="sm" className="border-white border-t-transparent" />}
                {busy ? 'Waiting for Kraken…' : 'Confirm'}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-wider text-gray-400" htmlFor="exit-stop">
                Stop (on Kraken)
              </label>
              <div className="mt-1 flex gap-2">
                <input id="exit-stop" type="number" inputMode="decimal" value={stop} onChange={(e) => setStop(e.target.value)} className={input} />
                <button
                  type="button"
                  disabled={!(priceFor('stop') > 0) || priceFor('stop') === current.stop_price}
                  onClick={() => setPending('stop')}
                  className={`${button} bg-primary-600 text-white hover:bg-primary-700`}
                >
                  Move stop
                </button>
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-gray-400" htmlFor="exit-target">
                Target
              </label>
              <div className="mt-1 flex gap-2">
                <input id="exit-target" type="number" inputMode="decimal" value={target} onChange={(e) => setTarget(e.target.value)} className={input} />
                <button
                  type="button"
                  disabled={!(priceFor('target') > 0) || priceFor('target') === current.target_price}
                  onClick={() => setPending('target')}
                  className={`${button} bg-primary-600 text-white hover:bg-primary-700`}
                >
                  Change target
                </button>
              </div>
            </div>
            <div className="flex flex-col gap-2 border-t border-terminal-border pt-4 sm:flex-row">
              {entryPending && (
                <button type="button" onClick={() => setPending('cancel')} className={`${button} flex-1 border border-amber-500/60 text-amber-200 hover:bg-amber-500/10`}>
                  Cancel pending entry
                </button>
              )}
              {holding && (
                <button type="button" onClick={() => setPending('close')} className={`${button} flex-1 bg-red-600 text-white hover:bg-red-700`}>
                  Close now
                </button>
              )}
            </div>
          </div>
        )}

        {done && (
          <p className="mt-4 rounded-md border border-green-500/40 bg-green-500/10 p-2 text-sm text-green-200" role="status">
            {done}
          </p>
        )}
        {error && (
          <p className="mt-4 rounded-md border border-red-500/40 bg-red-500/10 p-2 text-sm text-red-200" role="alert">
            {error}
          </p>
        )}
        {current.status_detail && (
          <p className="mt-3 text-xs text-amber-200">{current.status_detail}</p>
        )}
      </div>
    </div>
  )
}

export default ManageExitModal
